// NEC 2023 Chapter 9 Tables
// Table 1: Percent of Cross Section of Conduit and Tubing for Conductors
// Table 4: Dimensions of Conduit and Tubing
// Table 5: Dimensions of Insulated Conductors and Fixture Wires
// Table 8: Conductor Properties (DC Resistance)
// Table 9: AC Resistance and Reactance

import { ConduitType } from '../types';

// ============================================
// Table 1 - Conduit Fill Percentages
// ============================================
export const CONDUIT_FILL_PERCENT = {
  1: 53,   // One conductor: 53%
  2: 31,   // Two conductors: 31%
  3: 40,   // Three or more: 40%
} as const;

// NEC Table 1, Note 4: Short nipples (24" or less) may use 60% fill
export const NIPPLE_FILL_PERCENT = 60;

/**
 * Get maximum conduit fill percentage
 * @param numConductors - Number of conductors in conduit
 * @param isNipple - True if short nipple (24" or less) per NEC Table 1, Note 4
 * @returns Maximum fill percentage
 */
export function getMaxFillPercent(numConductors: number, isNipple: boolean = false): number {
  // Per NEC Table 1, Note 4: nipples 24" or less may use 60% fill
  if (isNipple) return NIPPLE_FILL_PERCENT;

  if (numConductors === 1) return 53;
  if (numConductors === 2) return 31;
  return 40;
}

// ============================================
// Table 4 - Conduit Internal Areas (square inches)
// ============================================
interface ConduitDimensions {
  internalDiameter: number;  // inches
  internalArea: number;      // square inches
}

// Conduit size in inches -> dimensions by type
// Note: 'Aluminum' conduit uses same dimensions as RMC (handled in lookup functions)
const TABLE_4: Record<Exclude<ConduitType, 'Aluminum'>, Record<number, ConduitDimensions>> = {
  PVC40: {
    0.5: { internalDiameter: 0.602, internalArea: 0.285 },
    0.75: { internalDiameter: 0.804, internalArea: 0.508 },
    1: { internalDiameter: 1.029, internalArea: 0.832 },
    1.25: { internalDiameter: 1.360, internalArea: 1.453 },
    1.5: { internalDiameter: 1.590, internalArea: 1.986 },
    2: { internalDiameter: 2.047, internalArea: 3.291 },
    2.5: { internalDiameter: 2.445, internalArea: 4.695 },
    3: { internalDiameter: 3.042, internalArea: 7.268 },
    3.5: { internalDiameter: 3.521, internalArea: 9.737 },
    4: { internalDiameter: 3.998, internalArea: 12.554 },
    5: { internalDiameter: 5.016, internalArea: 19.761 },
    6: { internalDiameter: 6.031, internalArea: 28.567 },
  },
  PVC80: {
    0.5: { internalDiameter: 0.526, internalArea: 0.217 },
    0.75: { internalDiameter: 0.722, internalArea: 0.409 },
    1: { internalDiameter: 0.936, internalArea: 0.688 },
    1.25: { internalDiameter: 1.255, internalArea: 1.237 },
    1.5: { internalDiameter: 1.476, internalArea: 1.711 },
    2: { internalDiameter: 1.913, internalArea: 2.874 },
    2.5: { internalDiameter: 2.290, internalArea: 4.119 },
    3: { internalDiameter: 2.864, internalArea: 6.442 },
    3.5: { internalDiameter: 3.326, internalArea: 8.688 },
    4: { internalDiameter: 3.786, internalArea: 11.258 },
    5: { internalDiameter: 4.768, internalArea: 17.855 },
    6: { internalDiameter: 5.709, internalArea: 25.598 },
  },
  RMC: {
    0.5: { internalDiameter: 0.632, internalArea: 0.314 },
    0.75: { internalDiameter: 0.836, internalArea: 0.549 },
    1: { internalDiameter: 1.063, internalArea: 0.887 },
    1.25: { internalDiameter: 1.394, internalArea: 1.526 },
    1.5: { internalDiameter: 1.624, internalArea: 2.071 },
    2: { internalDiameter: 2.083, internalArea: 3.408 },
    2.5: { internalDiameter: 2.489, internalArea: 4.866 },
    3: { internalDiameter: 3.090, internalArea: 7.499 },
    3.5: { internalDiameter: 3.570, internalArea: 10.010 },
    4: { internalDiameter: 4.050, internalArea: 12.882 },
    5: { internalDiameter: 5.073, internalArea: 20.212 },
    6: { internalDiameter: 6.093, internalArea: 29.158 },
  },
  EMT: {
    0.5: { internalDiameter: 0.622, internalArea: 0.304 },
    0.75: { internalDiameter: 0.824, internalArea: 0.533 },
    1: { internalDiameter: 1.049, internalArea: 0.864 },
    1.25: { internalDiameter: 1.380, internalArea: 1.496 },
    1.5: { internalDiameter: 1.610, internalArea: 2.036 },
    2: { internalDiameter: 2.067, internalArea: 3.356 },
    2.5: { internalDiameter: 2.731, internalArea: 5.858 },
    3: { internalDiameter: 3.356, internalArea: 8.846 },
    3.5: { internalDiameter: 3.834, internalArea: 11.545 },
    4: { internalDiameter: 4.334, internalArea: 14.753 },
  },
};

export function getConduitArea(size: number, type: ConduitType): number {
  // Aluminum conduit uses same dimensions as RMC for fill calculations
  const lookupType = type === 'Aluminum' ? 'RMC' : type;
  const conduit = TABLE_4[lookupType]?.[size];
  if (!conduit) {
    throw new Error(`Conduit size ${size}" ${type} not found`);
  }
  return conduit.internalArea;
}

export function getConduitDimensions(size: number, type: ConduitType): ConduitDimensions | undefined {
  // Aluminum conduit uses same dimensions as RMC for fill calculations
  const lookupType = type === 'Aluminum' ? 'RMC' : type;
  return TABLE_4[lookupType]?.[size];
}

// ============================================
// Table 5 - Conductor Dimensions by Insulation Type
// NEC 2023 Chapter 9, Table 5
// ============================================
interface ConductorDimensions {
  diameter: number;  // inches (overall including insulation)
  area: number;      // square inches
}

// Stranding type for conductor area lookup
export type ConductorStranding = 'standard' | 'compact';

// Insulation type for NEC Table 5 lookups
export type InsulationType = 'THHN' | 'THW' | 'XHHW' | 'RHH';

// Display names for insulation types
export const INSULATION_TYPE_DISPLAY: Record<InsulationType, string> = {
  'THHN': 'THHN/THWN-2',
  'THW': 'THW/THHW',
  'XHHW': 'XHHW/XHHW-2',
  'RHH': 'RHH/RHW-2',
};

// Short labels for conductor list display
export const INSULATION_TYPE_SHORT: Record<InsulationType, string> = {
  'THHN': 'THHN',
  'THW': 'THW',
  'XHHW': 'XHHW',
  'RHH': 'RHH',
};

// ============================================
// NEC Table 5 - THHN/THWN/THWN-2 Dimensions
// ============================================
const TABLE_5_THHN: Record<number, ConductorDimensions> = {
  [-14]: { diameter: 0.111, area: 0.0097 },
  [-12]: { diameter: 0.130, area: 0.0133 },
  [-10]: { diameter: 0.164, area: 0.0211 },
  [-8]: { diameter: 0.216, area: 0.0366 },
  [-6]: { diameter: 0.254, area: 0.0507 },
  [-4]: { diameter: 0.324, area: 0.0824 },
  [-3]: { diameter: 0.352, area: 0.0973 },
  [-2]: { diameter: 0.384, area: 0.1158 },
  [-1]: { diameter: 0.446, area: 0.1562 },
  [0]: { diameter: 0.486, area: 0.1855 },    // 1/0
  [0.5]: { diameter: 0.532, area: 0.2223 },  // 2/0
  [0.75]: { diameter: 0.584, area: 0.2679 }, // 3/0
  [0.9]: { diameter: 0.642, area: 0.3237 },  // 4/0
  [250]: { diameter: 0.711, area: 0.3970 },
  [300]: { diameter: 0.766, area: 0.4608 },
  [350]: { diameter: 0.817, area: 0.5242 },
  [400]: { diameter: 0.864, area: 0.5863 },
  [500]: { diameter: 0.949, area: 0.7073 },
  [600]: { diameter: 1.051, area: 0.8676 },
  [700]: { diameter: 1.122, area: 0.9887 },
  [750]: { diameter: 1.156, area: 1.0496 },
  [800]: { diameter: 1.188, area: 1.1085 },
  [900]: { diameter: 1.252, area: 1.2311 },
  [1000]: { diameter: 1.310, area: 1.3478 },
  // Not in Table 5 (THHN/THWN stops at 1000 kcmil). Carried so the designer's
  // 1250 kcmil option can be sized; the XHHW-2 dimensions are used as an estimate.
  [1250]: { diameter: 1.539, area: 1.8602 },
};

// Legacy alias for backward compatibility
const TABLE_5_THWN = TABLE_5_THHN;

// ============================================
// NEC Table 5 - THW/THHW/THW-2/TW Dimensions
// Note: 14-8 AWG same as XHHW, 6 AWG+ uses larger values
// ============================================
const TABLE_5_THW: Record<number, ConductorDimensions> = {
  [-14]: { diameter: 0.133, area: 0.0139 },
  [-12]: { diameter: 0.152, area: 0.0181 },
  [-10]: { diameter: 0.176, area: 0.0243 },
  [-8]: { diameter: 0.236, area: 0.0437 },
  [-6]: { diameter: 0.304, area: 0.0726 },
  [-4]: { diameter: 0.352, area: 0.0973 },
  [-3]: { diameter: 0.380, area: 0.1134 },
  [-2]: { diameter: 0.412, area: 0.1333 },
  [-1]: { diameter: 0.492, area: 0.1901 },
  [0]: { diameter: 0.532, area: 0.2223 },    // 1/0
  [0.5]: { diameter: 0.578, area: 0.2624 },  // 2/0
  [0.75]: { diameter: 0.630, area: 0.3117 }, // 3/0
  [0.9]: { diameter: 0.688, area: 0.3718 },  // 4/0
  [250]: { diameter: 0.765, area: 0.4596 },
  [300]: { diameter: 0.820, area: 0.5281 },
  [350]: { diameter: 0.871, area: 0.5958 },
  [400]: { diameter: 0.918, area: 0.6619 },
  [500]: { diameter: 1.003, area: 0.7901 },
  [600]: { diameter: 1.113, area: 0.9729 },
  [700]: { diameter: 1.184, area: 1.1010 },
  [750]: { diameter: 1.218, area: 1.1652 },
  [800]: { diameter: 1.250, area: 1.2272 },
  [900]: { diameter: 1.314, area: 1.3561 },
  [1000]: { diameter: 1.372, area: 1.4784 },
  [1250]: { diameter: 1.539, area: 1.8602 },
};

// ============================================
// NEC Table 5 - XHHW/XHHW-2/XHH Dimensions
// ============================================
const TABLE_5_XHHW: Record<number, ConductorDimensions> = {
  [-14]: { diameter: 0.133, area: 0.0139 },
  [-12]: { diameter: 0.152, area: 0.0181 },
  [-10]: { diameter: 0.176, area: 0.0243 },
  [-8]: { diameter: 0.236, area: 0.0437 },
  [-6]: { diameter: 0.274, area: 0.0590 },
  [-4]: { diameter: 0.322, area: 0.0814 },
  [-3]: { diameter: 0.350, area: 0.0962 },
  [-2]: { diameter: 0.382, area: 0.1146 },
  [-1]: { diameter: 0.442, area: 0.1534 },
  [0]: { diameter: 0.482, area: 0.1825 },    // 1/0
  [0.5]: { diameter: 0.528, area: 0.2190 },  // 2/0
  [0.75]: { diameter: 0.580, area: 0.2642 }, // 3/0
  [0.9]: { diameter: 0.638, area: 0.3197 },  // 4/0
  [250]: { diameter: 0.705, area: 0.3904 },
  [300]: { diameter: 0.760, area: 0.4536 },
  [350]: { diameter: 0.811, area: 0.5166 },
  [400]: { diameter: 0.858, area: 0.5782 },
  [500]: { diameter: 0.943, area: 0.6984 },
  [600]: { diameter: 1.053, area: 0.8709 },
  [700]: { diameter: 1.124, area: 0.9923 },
  [750]: { diameter: 1.158, area: 1.0532 },
  [800]: { diameter: 1.190, area: 1.1122 },
  [900]: { diameter: 1.254, area: 1.2351 },
  [1000]: { diameter: 1.312, area: 1.3519 },
  [1250]: { diameter: 1.479, area: 1.7180 },
};

// ============================================
// NEC Table 5 - RHH/RHW/RHW-2 Dimensions (with outer covering)
// ============================================
const TABLE_5_RHH: Record<number, ConductorDimensions> = {
  [-14]: { diameter: 0.193, area: 0.0293 },
  [-12]: { diameter: 0.212, area: 0.0353 },
  [-10]: { diameter: 0.236, area: 0.0437 },
  [-8]: { diameter: 0.326, area: 0.0835 },
  [-6]: { diameter: 0.364, area: 0.1041 },
  [-4]: { diameter: 0.412, area: 0.1333 },
  [-3]: { diameter: 0.440, area: 0.1521 },
  [-2]: { diameter: 0.472, area: 0.1750 },
  [-1]: { diameter: 0.582, area: 0.2660 },
  [0]: { diameter: 0.622, area: 0.3039 },    // 1/0
  [0.5]: { diameter: 0.668, area: 0.3505 },  // 2/0
  [0.75]: { diameter: 0.720, area: 0.4072 }, // 3/0
  [0.9]: { diameter: 0.778, area: 0.4754 },  // 4/0
  [250]: { diameter: 0.895, area: 0.6291 },
  [300]: { diameter: 0.950, area: 0.7088 },
  [350]: { diameter: 1.001, area: 0.7870 },
  [400]: { diameter: 1.048, area: 0.8626 },
  [500]: { diameter: 1.133, area: 1.0082 },
  [600]: { diameter: 1.243, area: 1.2135 },
  [700]: { diameter: 1.314, area: 1.3561 },
  [750]: { diameter: 1.348, area: 1.4272 },
  [800]: { diameter: 1.380, area: 1.4957 },
  [900]: { diameter: 1.444, area: 1.6377 },
  [1000]: { diameter: 1.502, area: 1.7719 },
  [1250]: { diameter: 1.729, area: 2.3479 },
};

// Combined lookup by insulation type
const TABLE_5_BY_INSULATION: Record<InsulationType, Record<number, ConductorDimensions>> = {
  'THHN': TABLE_5_THHN,
  'THW': TABLE_5_THW,
  'XHHW': TABLE_5_XHHW,
  'RHH': TABLE_5_RHH,
};

// ============================================
// Table 5A - Compact Stranded Conductor Dimensions
// NEC 2023 Chapter 9, Table 5A
// "Compact Copper and Aluminum Building Wire Nominal Dimensions and Areas"
// Covers multiple insulation types (THW, THHN, XHHW, etc.) for compact stranded conductors
// Compact stranding results in smaller conductor OD than standard stranding
// Note: These values are based on Type XHHW dimensions
// ============================================
const TABLE_5A_COMPACT: Record<number, ConductorDimensions> = {
  // Compact stranding available 8 AWG and larger (per NEC Table 5A)
  [-8]: { diameter: 0.224, area: 0.0394 },
  [-6]: { diameter: 0.260, area: 0.0530 },
  [-4]: { diameter: 0.305, area: 0.0730 },
  [-2]: { diameter: 0.360, area: 0.1017 },
  [-1]: { diameter: 0.415, area: 0.1352 },
  [0]: { diameter: 0.450, area: 0.1590 },    // 1/0
  [0.5]: { diameter: 0.490, area: 0.1885 },  // 2/0
  [0.75]: { diameter: 0.540, area: 0.2290 }, // 3/0
  [0.9]: { diameter: 0.590, area: 0.2733 },  // 4/0
  [250]: { diameter: 0.660, area: 0.3421 },
  [300]: { diameter: 0.715, area: 0.4015 },
  [350]: { diameter: 0.760, area: 0.4536 },
  [400]: { diameter: 0.800, area: 0.5026 },
  [500]: { diameter: 0.880, area: 0.6082 },
  [600]: { diameter: 0.980, area: 0.7542 },
  [700]: { diameter: 1.050, area: 0.8659 },
  [750]: { diameter: 1.090, area: 0.9331 },
  [900]: { diameter: 1.169, area: 1.0733 },
  [1000]: { diameter: 1.230, area: 1.1882 },
};

/**
 * Get conductor area from NEC Table 5 or Table 5A
 * @param wireSize - Wire size in internal format
 * @param stranding - 'standard' (Table 5) or 'compact' (Table 5A)
 * @param insulationType - Insulation type (THHN, THW, XHHW, RHH)
 * @returns Conductor area in square inches
 */
export function getConductorArea(
  wireSize: number,
  stranding: ConductorStranding = 'standard',
  insulationType: InsulationType = 'THHN'
): number {
  if (stranding === 'compact' && insulationType === 'XHHW') {
    // Table 5A compact areas carried here are the XHHW-2 column. THW and
    // THHN/THWN compact conductors have larger areas; until those columns are
    // added they fall through to the (larger) standard Table 5 area, which is
    // conservative for conduit fill.
    const compact = TABLE_5A_COMPACT[wireSize];
    if (compact) {
      return compact.area;
    }
    // compact stranding only available 8 AWG and larger
  }

  // Look up in the appropriate insulation type table
  const table = TABLE_5_BY_INSULATION[insulationType];
  const conductor = table?.[wireSize];
  if (!conductor) {
    throw new Error(`Wire size ${wireSize} not found in Table 5 for ${insulationType}`);
  }
  return conductor.area;
}

/**
 * Get conductor diameter from NEC Table 5 or Table 5A
 * @param wireSize - Wire size in internal format
 * @param stranding - 'standard' (Table 5) or 'compact' (Table 5A)
 * @param insulationType - Insulation type (THHN, THW, XHHW, RHH)
 * @returns Conductor diameter in inches
 */
export function getConductorDiameter(
  wireSize: number,
  stranding: ConductorStranding = 'standard',
  insulationType: InsulationType = 'THHN'
): number {
  // Same rule as getConductorArea: the Table 5A column carried is XHHW-2.
  if (stranding === 'compact' && insulationType === 'XHHW') {
    const compact = TABLE_5A_COMPACT[wireSize];
    if (compact) {
      return compact.diameter;
    }
  }

  // Look up in the appropriate insulation type table
  const table = TABLE_5_BY_INSULATION[insulationType];
  const conductor = table?.[wireSize];
  if (!conductor) {
    throw new Error(`Wire size ${wireSize} not found in Table 5 for ${insulationType}`);
  }
  return conductor.diameter;
}

/**
 * Check if compact stranding is available for a wire size
 * Compact stranding is only available for 8 AWG and larger
 */
export function isCompactStrandingAvailable(wireSize: number): boolean {
  return wireSize in TABLE_5A_COMPACT;
}

// ============================================
// Table 8 - DC Resistance (Ohms/1000ft at 75°C)
// ============================================
interface DCResistance {
  copperUncoated: number;
  copperCoated: number;
  aluminum: number;
}

const TABLE_8_DC_RESISTANCE: Record<number, DCResistance> = {
  [-14]: { copperUncoated: 3.14, copperCoated: 3.26, aluminum: 5.17 },
  [-12]: { copperUncoated: 1.98, copperCoated: 2.05, aluminum: 3.25 },
  [-10]: { copperUncoated: 1.24, copperCoated: 1.29, aluminum: 2.04 },
  [-8]: { copperUncoated: 0.778, copperCoated: 0.809, aluminum: 1.28 },
  [-6]: { copperUncoated: 0.491, copperCoated: 0.510, aluminum: 0.808 },
  [-4]: { copperUncoated: 0.308, copperCoated: 0.321, aluminum: 0.508 },
  [-3]: { copperUncoated: 0.245, copperCoated: 0.254, aluminum: 0.403 },
  [-2]: { copperUncoated: 0.194, copperCoated: 0.201, aluminum: 0.319 },
  [-1]: { copperUncoated: 0.154, copperCoated: 0.160, aluminum: 0.253 },
  [0]: { copperUncoated: 0.122, copperCoated: 0.127, aluminum: 0.201 },
  [0.5]: { copperUncoated: 0.0967, copperCoated: 0.101, aluminum: 0.159 },
  [0.75]: { copperUncoated: 0.0766, copperCoated: 0.0797, aluminum: 0.126 },
  [0.9]: { copperUncoated: 0.0608, copperCoated: 0.0626, aluminum: 0.100 },
  [250]: { copperUncoated: 0.0515, copperCoated: 0.0535, aluminum: 0.0847 },
  [300]: { copperUncoated: 0.0429, copperCoated: 0.0446, aluminum: 0.0707 },
  [350]: { copperUncoated: 0.0367, copperCoated: 0.0382, aluminum: 0.0605 },
  [400]: { copperUncoated: 0.0321, copperCoated: 0.0331, aluminum: 0.0529 },
  [500]: { copperUncoated: 0.0258, copperCoated: 0.0265, aluminum: 0.0424 },
  [600]: { copperUncoated: 0.0214, copperCoated: 0.0223, aluminum: 0.0353 },
  [700]: { copperUncoated: 0.0184, copperCoated: 0.0189, aluminum: 0.0303 },
  [750]: { copperUncoated: 0.0171, copperCoated: 0.0176, aluminum: 0.0282 },
  [800]: { copperUncoated: 0.0161, copperCoated: 0.0166, aluminum: 0.0265 },
  [900]: { copperUncoated: 0.0143, copperCoated: 0.0147, aluminum: 0.0235 },
  [1000]: { copperUncoated: 0.0129, copperCoated: 0.0132, aluminum: 0.0212 },
  [1250]: { copperUncoated: 0.0103, copperCoated: 0.0106, aluminum: 0.0169 },
};

export function getDCResistance(wireSize: number, material: 'copper' | 'aluminum'): number {
  const entry = TABLE_8_DC_RESISTANCE[wireSize];
  if (!entry) {
    throw new Error(`Wire size ${wireSize} not found in Table 8`);
  }
  return material === 'copper' ? entry.copperUncoated : entry.aluminum;
}

// ============================================
// Table 9 - AC Resistance and Reactance
// For 600V, 3-phase, 60 Hz, 75°C (167°F)
// Three Single Conductors in Conduit
// Ohms per 1000 feet
//
// NEC 2023 Chapter 9, Table 9 - Complete data with copper and aluminum wire
// Values for PVC, Aluminum, and Steel conduit types
// ============================================
interface ACProperties {
  // Reactance (same for copper and aluminum wire)
  reactance: {
    pvc: number;      // PVC conduit
    aluminum: number; // Aluminum conduit
    steel: number;    // Steel conduit (RMC/EMT)
  };
  // AC Resistance for copper wire
  copperResistance: {
    pvc: number;
    aluminum: number;
    steel: number;
  };
  // AC Resistance for aluminum wire (null if not available for this size)
  aluminumResistance: {
    pvc: number;
    aluminum: number;
    steel: number;
  } | null;
}

const TABLE_9_AC: Record<number, ACProperties> = {
  [-14]: {
    reactance: { pvc: 0.058, aluminum: 0.058, steel: 0.073 },
    copperResistance: { pvc: 3.1, aluminum: 3.1, steel: 3.1 },
    aluminumResistance: null, // Not available for #14
  },
  [-12]: {
    reactance: { pvc: 0.054, aluminum: 0.054, steel: 0.068 },
    copperResistance: { pvc: 2.0, aluminum: 2.0, steel: 2.0 },
    aluminumResistance: { pvc: 3.2, aluminum: 3.2, steel: 3.2 },
  },
  [-10]: {
    reactance: { pvc: 0.050, aluminum: 0.050, steel: 0.063 },
    copperResistance: { pvc: 1.2, aluminum: 1.2, steel: 1.2 },
    aluminumResistance: { pvc: 2.0, aluminum: 2.0, steel: 2.0 },
  },
  [-8]: {
    reactance: { pvc: 0.052, aluminum: 0.052, steel: 0.065 },
    copperResistance: { pvc: 0.78, aluminum: 0.78, steel: 0.78 },
    aluminumResistance: { pvc: 1.3, aluminum: 1.3, steel: 1.3 },
  },
  [-6]: {
    reactance: { pvc: 0.051, aluminum: 0.051, steel: 0.064 },
    copperResistance: { pvc: 0.49, aluminum: 0.49, steel: 0.49 },
    aluminumResistance: { pvc: 0.81, aluminum: 0.81, steel: 0.81 },
  },
  [-4]: {
    reactance: { pvc: 0.048, aluminum: 0.048, steel: 0.060 },
    copperResistance: { pvc: 0.31, aluminum: 0.31, steel: 0.31 },
    aluminumResistance: { pvc: 0.51, aluminum: 0.51, steel: 0.51 },
  },
  [-3]: {
    reactance: { pvc: 0.047, aluminum: 0.047, steel: 0.059 },
    copperResistance: { pvc: 0.25, aluminum: 0.25, steel: 0.25 },
    aluminumResistance: { pvc: 0.40, aluminum: 0.41, steel: 0.40 },
  },
  [-2]: {
    reactance: { pvc: 0.045, aluminum: 0.045, steel: 0.057 },
    copperResistance: { pvc: 0.19, aluminum: 0.20, steel: 0.20 },
    aluminumResistance: { pvc: 0.32, aluminum: 0.32, steel: 0.32 },
  },
  [-1]: {
    reactance: { pvc: 0.046, aluminum: 0.046, steel: 0.057 },
    copperResistance: { pvc: 0.15, aluminum: 0.16, steel: 0.16 },
    aluminumResistance: { pvc: 0.25, aluminum: 0.26, steel: 0.25 },
  },
  [0]: { // 1/0
    reactance: { pvc: 0.044, aluminum: 0.044, steel: 0.055 },
    copperResistance: { pvc: 0.12, aluminum: 0.13, steel: 0.12 },
    aluminumResistance: { pvc: 0.20, aluminum: 0.21, steel: 0.20 },
  },
  [0.5]: { // 2/0
    reactance: { pvc: 0.043, aluminum: 0.043, steel: 0.054 },
    copperResistance: { pvc: 0.10, aluminum: 0.10, steel: 0.10 },
    aluminumResistance: { pvc: 0.16, aluminum: 0.16, steel: 0.16 },
  },
  [0.75]: { // 3/0
    reactance: { pvc: 0.042, aluminum: 0.042, steel: 0.052 },
    copperResistance: { pvc: 0.077, aluminum: 0.082, steel: 0.079 },
    aluminumResistance: { pvc: 0.13, aluminum: 0.13, steel: 0.13 },
  },
  [0.9]: { // 4/0
    reactance: { pvc: 0.041, aluminum: 0.041, steel: 0.051 },
    copperResistance: { pvc: 0.062, aluminum: 0.067, steel: 0.063 },
    aluminumResistance: { pvc: 0.10, aluminum: 0.11, steel: 0.10 },
  },
  [250]: {
    reactance: { pvc: 0.041, aluminum: 0.041, steel: 0.052 },
    copperResistance: { pvc: 0.052, aluminum: 0.057, steel: 0.054 },
    aluminumResistance: { pvc: 0.085, aluminum: 0.090, steel: 0.086 },
  },
  [300]: {
    reactance: { pvc: 0.041, aluminum: 0.041, steel: 0.051 },
    copperResistance: { pvc: 0.044, aluminum: 0.049, steel: 0.045 },
    aluminumResistance: { pvc: 0.071, aluminum: 0.076, steel: 0.072 },
  },
  [350]: {
    reactance: { pvc: 0.040, aluminum: 0.040, steel: 0.050 },
    copperResistance: { pvc: 0.038, aluminum: 0.043, steel: 0.039 },
    aluminumResistance: { pvc: 0.061, aluminum: 0.066, steel: 0.063 },
  },
  [400]: {
    reactance: { pvc: 0.040, aluminum: 0.040, steel: 0.049 },
    copperResistance: { pvc: 0.033, aluminum: 0.038, steel: 0.035 },
    aluminumResistance: { pvc: 0.054, aluminum: 0.059, steel: 0.055 },
  },
  [500]: {
    reactance: { pvc: 0.039, aluminum: 0.039, steel: 0.048 },
    copperResistance: { pvc: 0.027, aluminum: 0.032, steel: 0.029 },
    aluminumResistance: { pvc: 0.043, aluminum: 0.048, steel: 0.045 },
  },
  [600]: {
    reactance: { pvc: 0.039, aluminum: 0.039, steel: 0.048 },
    copperResistance: { pvc: 0.023, aluminum: 0.028, steel: 0.025 },
    aluminumResistance: { pvc: 0.036, aluminum: 0.041, steel: 0.038 },
  },
  [700]: {
    // Interpolated values between 600 and 750
    reactance: { pvc: 0.0385, aluminum: 0.0385, steel: 0.048 },
    copperResistance: { pvc: 0.021, aluminum: 0.026, steel: 0.023 },
    aluminumResistance: { pvc: 0.0325, aluminum: 0.0375, steel: 0.0345 },
  },
  [750]: {
    reactance: { pvc: 0.038, aluminum: 0.038, steel: 0.048 },
    copperResistance: { pvc: 0.019, aluminum: 0.024, steel: 0.021 },
    aluminumResistance: { pvc: 0.029, aluminum: 0.034, steel: 0.031 },
  },
  [800]: {
    // Interpolated values between 750 and 1000
    reactance: { pvc: 0.0375, aluminum: 0.0375, steel: 0.047 },
    copperResistance: { pvc: 0.018, aluminum: 0.022, steel: 0.020 },
    aluminumResistance: { pvc: 0.027, aluminum: 0.032, steel: 0.029 },
  },
  [900]: {
    // Interpolated values between 750 and 1000
    reactance: { pvc: 0.037, aluminum: 0.037, steel: 0.0465 },
    copperResistance: { pvc: 0.0165, aluminum: 0.0205, steel: 0.019 },
    aluminumResistance: { pvc: 0.025, aluminum: 0.0295, steel: 0.027 },
  },
  [1000]: {
    reactance: { pvc: 0.037, aluminum: 0.037, steel: 0.046 },
    copperResistance: { pvc: 0.015, aluminum: 0.019, steel: 0.018 },
    aluminumResistance: { pvc: 0.023, aluminum: 0.027, steel: 0.025 },
  },
  [1250]: {
    // Extrapolated from 1000
    reactance: { pvc: 0.036, aluminum: 0.036, steel: 0.044 },
    copperResistance: { pvc: 0.012, aluminum: 0.015, steel: 0.014 },
    aluminumResistance: { pvc: 0.018, aluminum: 0.022, steel: 0.020 },
  },
};

// Helper to get conduit category from conduit type (for Table 9 lookups)
export function getConduitCategory(conduitType: ConduitType): 'pvc' | 'aluminum' | 'steel' {
  switch (conduitType) {
    case 'PVC40':
    case 'PVC80':
      return 'pvc';
    case 'Aluminum':
      return 'aluminum';
    case 'RMC':
    case 'EMT':
    default:
      return 'steel';
  }
}

export function getACResistance(
  wireSize: number,
  conduitType: ConduitType,
  material: 'copper' | 'aluminum' = 'copper'
): number {
  const entry = TABLE_9_AC[wireSize];
  if (!entry) {
    throw new Error(`Wire size ${wireSize} not found in Table 9`);
  }

  const category = getConduitCategory(conduitType);

  if (material === 'aluminum') {
    // Use aluminum wire resistance values from Table 9
    if (entry.aluminumResistance) {
      return entry.aluminumResistance[category];
    }
    // Fall back to scaled copper resistance for sizes without aluminum data
    const dcEntry = TABLE_8_DC_RESISTANCE[wireSize];
    if (dcEntry && dcEntry.aluminum > 0 && dcEntry.copperUncoated > 0) {
      return entry.copperResistance[category] * (dcEntry.aluminum / dcEntry.copperUncoated);
    }
  }

  return entry.copperResistance[category];
}

export function getReactance(wireSize: number, conduitType: ConduitType): number {
  const entry = TABLE_9_AC[wireSize];
  if (!entry) {
    throw new Error(`Wire size ${wireSize} not found in Table 9`);
  }

  const category = getConduitCategory(conduitType);
  return entry.reactance[category];
}

// NEC References
export const NEC_CHAPTER_9_TABLE_1_REF = {
  table: 'Chapter 9, Table 1',
  article: 'Chapter 9',
  description: 'Percent of Cross Section of Conduit and Tubing for Conductors',
};

export const NEC_CHAPTER_9_TABLE_4_REF = {
  table: 'Chapter 9, Table 4',
  article: 'Chapter 9',
  description: 'Dimensions of Conduit and Tubing',
};

export const NEC_CHAPTER_9_TABLE_5_REF = {
  table: 'Chapter 9, Table 5',
  article: 'Chapter 9',
  description: 'Dimensions of Insulated Conductors and Fixture Wires',
};

export const NEC_CHAPTER_9_TABLE_5A_REF = {
  table: 'Chapter 9, Table 5A',
  article: 'Chapter 9',
  description: 'Compact Aluminum Building Wire Nominal Dimensions',
};

export const NEC_CHAPTER_9_TABLE_8_REF = {
  table: 'Chapter 9, Table 8',
  article: 'Chapter 9',
  description: 'Conductor Properties - DC Resistance',
};

export const NEC_CHAPTER_9_TABLE_9_REF = {
  table: 'Chapter 9, Table 9',
  article: 'Chapter 9',
  description: 'AC Resistance and Reactance for 600-Volt Cables',
};

export {
  TABLE_4,
  TABLE_5_THWN,
  TABLE_5_THHN,
  TABLE_5_THW,
  TABLE_5_XHHW,
  TABLE_5_RHH,
  TABLE_5_BY_INSULATION,
  TABLE_5A_COMPACT,
  TABLE_8_DC_RESISTANCE,
  TABLE_9_AC
};
